import React, { useState, useMemo, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Stepper } from '@shared/components/Stepper';
import { ReviewPaymentStep } from './stepComponents/ReviewPaymentStep';
import { CustomerInformationStep } from './stepComponents/CustomerInformationStep';
import { ServiceDetailsStep } from './stepComponents/ServiceDetailsStep';
import { ServiceSelectionStep } from './stepComponents/ServiceSelectionStep';
import { useRemoteUtilityId } from '@shared/selectors/globalSelectors';
import { useFormCompletionTracker } from '@shared/hooks/useFormCompletionTracker';
import { P } from 'framer-motion/dist/types.d-B_QPEvFK';

export const CreateServiceRequest: React.FC = () => {
  const remoteUtilityId = useRemoteUtilityId();
  const params = useParams();
  const storageKey = `service-request-progress-${remoteUtilityId}`;

  // Extract consumerId and consumerNo from URL parameters
  const urlConsumerId = params.consumerId ? parseInt(params.consumerId) : null;
  const urlConsumerNo = params.consumerNo || null;

  const { startTracking, logTimeUntilStep, stopTracking } = useFormCompletionTracker("Service");

  useEffect(() => {startTracking();}, [startTracking])

  // Memoize the steps array to prevent infinite re-renders
  const steps = useMemo(
    () => [
      {
        title: "Customer Information",
        slug: "customer-info", // Add slug for routing
        component: (
          <CustomerInformationStep
            remoteUtilityId={Number(remoteUtilityId)}
            storageKey={storageKey}
            urlConsumerId={urlConsumerId}
            urlConsumerNo={urlConsumerNo}
          />
        ),
        optional: false,
        infoTitle: "Customer Information Help",
        infoDescription:
          "Enter the customer's account details. You can use the 'Look Up' button to automatically populate fields if you have the account number.",
      },
      {
        title: "Select Service",
        slug: "service-selection", // Add slug for routing
        component: (
          <ServiceSelectionStep
            remoteUtilityId={Number(remoteUtilityId)}
            storageKey={storageKey}
            onNext={() => {}}
            onPrevious={() => {}}
          />
        ),
        optional: false,
        infoTitle: "Service Selection Help",
        infoDescription:
          "Browse and select the service you need. Use the search bar to quickly find specific services. Each service shows pricing and description to help you choose.",
      },
      {
        title: "Service Details",
        slug: "service-details", // Add slug for routing
        component: (
          <ServiceDetailsStep
            remoteUtilityId={Number(remoteUtilityId)}
            storageKey={storageKey}
          />
        ),
        optional: false,
        infoTitle: "Service Details Help",
        infoDescription:
          "Review your selected service details and configure scheduling preferences. You can also add any special instructions for the service team.",
      },
      {
        title: "Review & Payment",
        slug: "review-payment", // Add slug for routing
        component: (
          <ReviewPaymentStep
            remoteUtilityId={Number(remoteUtilityId)}
            storageKey={storageKey}
            stopTracking={stopTracking}
          />
        ),
        optional: false,
        infoTitle: "Review & Payment Help",
        infoDescription:
          "Review all your service details and payment information before submitting your request.",
      },
    ],
    [remoteUtilityId, storageKey, urlConsumerId, urlConsumerNo, stopTracking]
  ); // Added URL params to dependencies

  return (
    <div className="flex flex-col gap-5">
      <div >
        <p className="page-title">
          Create Service Request
        </p>
        <p className="page-subtitle">
          Complete the following steps to submit a service request.
        </p>
      </div>

      <Stepper
        steps={steps}
        onStepChange={() => {}}
        onStepSave={async (stepIndex, stepData, isDirty) => {
          // Storage is handled at component level using step helpers
        }}
        enableApiSave={false}
        saveOnStepChange={false}
        trackDirtyState={false}
        autoSave={true}
        persistProgress={true}
        allowSkip={false} // Set to false since all steps are required
        storageKey={storageKey}
        enableRouting={true} // Enable hash-based routing (no 404s on refresh)
        onValidation={undefined} // basePath is not needed for hash-based routing
        onComplete={() => {}}
        showDraftResume={true}
      />
    </div>
  );
};